// src/pages/VendaDeptoSecao.jsx
import React, { useState, useEffect, useCallback } from "react";
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer/index.jsx";
import { api } from "../../services/api.js";
import Swal from 'sweetalert2';
import {
  FaChevronDown,
  FaChevronUp,
  FaUndoAlt,
  FaCalendarAlt,
  FaFilter,
} from "react-icons/fa";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './vendadepto.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// ================= FORMATADORES E UTILITÁRIOS =================
const formatMoeda = (v) => 
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(v ?? 0);

const formatPercent = (v) => 
  isNaN(v) || v === null ? "0,00%" : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const formatLoja = (n) => String(n).padStart(2, '0');

const formatarMesExibicao = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return mesAno;
  return `${mesAno.slice(2)}/20${mesAno.slice(0, 2)}`;
};

const formatarParaInput = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return "";
  return `20${mesAno.slice(0, 2)}-${mesAno.slice(2)}`;
};

// Componente de Garrafa / Progresso Meta
const MetaProgressoGarrafa = ({ percentual }) => {
  const pct = Math.min(Math.max(percentual || 0, 0), 100);
  return (
    <div className="bottle-wrapper">
      <div className="bottle-cap"></div>
      <div className="bottle-container" title={`Atingido: ${pct.toFixed(1)}%`}>
        <div className="bottle-fill" style={{ height: `${pct}%` }}></div>
      </div>
      <span className="bottle-badge">{pct.toFixed(1)}%</span>
    </div>
  );
};

// Modal Generico Padronizado
function ModalGenerico({ show, onClose, title, children }) {
  if (!show) return null;
  return (
    <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 1200, backdropFilter: 'blur(4px)' }}>
      <div className="modal-dialog modal-md modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px', overflow: 'hidden' }}>
          <div className="modal-header bg-custom-red text-white py-3 px-4">
            <h5 className="modal-title font-weight-bold fs-6 m-0 text-white">{title}</h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body p-4">{children}</div>
          <div className="modal-footer bg-light py-2 px-4">
            <button type="button" className="btn btn-custom-red btn-sm px-4 fw-semibold" onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal Loading Padronizado
function LoadingModal({ show, message }) {
  if (!show) return null;
  return (
    <div className="loading-modal-overlay">
      <div className="loading-modal-content">
        <div className="spinner-border text-custom-red" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
          <span className="visually-hidden">A carregar...</span>
        </div>
        <div className="loading-text mt-3 fw-bold text-secondary">{message}</div>
      </div>
    </div>
  );
}

const VendaDeptoSecao = () => {
  const [mes, setMes] = useState("");
  const [dadosDepto, setDadosDepto] = useState([]);
  const [dadosSecao, setDadosSecao] = useState({});
  const [dadosLoja, setDadosLoja] = useState({});
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [tituloGrafico, setTituloGrafico] = useState("Departamentos");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("A carregar dados...");
  const [showModal, setShowModal] = useState(false);
  
  // 🔹 Estado do Filtro "Somente com Meta" (Ativado por padrão)
  const [somenteComMeta, setSomenteComMeta] = useState(true);

  const isMobile = window.innerWidth < 768;

  const carregarDepto = useCallback(async (mesConsulta) => {
    setLoading(true);
    setLoadingMessage("A carregar departamentos...");
    setDadosSecao({});
    setDadosLoja({});
    
    try {
      const mesRef = mesConsulta || mes;
      const chapa = localStorage.getItem("chapa");
      const { data } = await api.get(`/venda/mes/depto`, { params: { mes: mesRef, usuario: chapa } });
      
      const lista = data || [];
      setDadosDepto(lista);
      setTituloGrafico("Departamentos");
    } catch (e) { 
      console.error(e);
      Swal.fire('Erro', 'Não foi possível carregar as vendas por departamento.', 'error');
    } finally {
      setLoading(false);
    }
  }, [mes]);

  useEffect(() => {
    const hoje = new Date();
    const aa = String(hoje.getFullYear()).slice(2);
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const mesPadrao = `${aa}${mm}`;
    setMes(mesPadrao);
    carregarDepto(mesPadrao);
  }, [carregarDepto]);

  const buscarSecao = async (depCod, depNome) => {
    if (dadosSecao[depCod]) {
      const copy = { ...dadosSecao }; 
      delete copy[depCod];
      setDadosSecao(copy);
      return;
    }
    setLoading(true);
    setLoadingMessage(`A carregar seções: ${depNome}...`);
    try {
      const chapa = localStorage.getItem("chapa");
      const { data } = await api.get(`/venda/mes/deptosecao`, { params: { mes, usuario: chapa, depto: depCod } });
      const lista = data || [];
      setDadosSecao(prev => ({ ...prev, [depCod]: lista }));
      setTituloGrafico(`Seções: ${depNome}`);
    } catch (e) { 
      console.error(e); 
    } finally {
      setLoading(false);
    }
  };

  const buscarLoja = async (depCod, secCod, secNome) => {
    const key = `${depCod}-${secCod}`;
    if (dadosLoja[key]) {
      const copy = { ...dadosLoja }; 
      delete copy[key];
      setDadosLoja(copy);
      return;
    }
    setLoading(true);
    setLoadingMessage(`A carregar lojas: ${secNome}...`);
    try {
      const chapa = localStorage.getItem("chapa");
      const { data } = await api.get(`/venda/mes/deptosecaoloja`, { params: { mes, usuario: chapa, depto: depCod, secao: secCod } });
      const lista = data || [];
      setDadosLoja(prev => ({ ...prev, [key]: lista }));
      setTituloGrafico(`Lojas: ${secNome}`);
    } catch (e) { 
      console.error(e); 
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Filtragem por "Somente com Meta"
  const departamentosFiltrados = dadosDepto.filter(d => !somenteComMeta || (d.meta && d.meta > 0));

  const getSecoesFiltradas = (depCod) => {
    const secoes = dadosSecao[depCod] || [];
    return secoes.filter(s => !somenteComMeta || (s.meta && s.meta > 0));
  };

  const getLojasFiltradas = (key) => {
    const lojas = dadosLoja[key] || [];
    return lojas.filter(l => !somenteComMeta || (l.meta && l.meta > 0));
  };

  // Atualização do gráfico com base nos itens filtrados
  useEffect(() => {
    setDadosGrafico(departamentosFiltrados.map(d => ({
      nome: d.ddepartamento?.split("-")[1] || d.ddepartamento || "Depto",
      atingido: d.atingido ?? 0,
      venda: d.venda ?? 0,
      meta: d.meta ?? 0,
      margem: d.margem ?? 0
    })));
  }, [dadosDepto, somenteComMeta]);

  // Cálculo do Total Geral dos Departamentos
  const resumoGeral = departamentosFiltrados.reduce((acc, curr) => {
    acc.venda += (curr.venda || 0);
    acc.meta += (curr.meta || 0);
    acc.somaMargemVenda += ((curr.margem || 0) * (curr.venda || 0));
    return acc;
  }, { venda: 0, meta: 0, somaMargemVenda: 0 });

  const atingidoGeral = resumoGeral.meta > 0 ? (resumoGeral.venda / resumoGeral.meta) * 100 : 0;
  const margemGeral = resumoGeral.venda > 0 ? (resumoGeral.somaMargemVenda / resumoGeral.venda) : 0;

  // Configuração Chart.js
  const chartData = {
    labels: dadosGrafico.map(d => d.nome),
    datasets: [
      {
        type: 'bar',
        label: '% Atingido',
        data: dadosGrafico.map(d => d.atingido),
        backgroundColor: dadosGrafico.map(d => d.atingido >= 100 ? 'rgba(16, 185, 129, 0.85)' : 'rgba(2, 132, 199, 0.85)'),
        borderColor: dadosGrafico.map(d => d.atingido >= 100 ? 'rgba(16, 185, 129, 1)' : 'rgba(2, 132, 199, 1)'),
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'y'
      },
      {
        type: 'line',
        label: '% Margem',
        data: dadosGrafico.map(d => d.margem),
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.3,
        yAxisID: 'y1'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { font: { size: 10, weight: '600' } }
      },
      title: {
        display: true,
        text: `${tituloGrafico} (% Atingido & % Margem)`,
        font: { size: 13, weight: 'bold' },
        color: '#0f172a'
      },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 8,
        callbacks: {
          label: (ctx) => {
            const idx = ctx.dataIndex;
            const item = dadosGrafico[idx];
            if (!item) return `${ctx.dataset.label}: ${ctx.raw}%`;
            if (ctx.dataset.type === 'line') {
              return `% Margem: ${formatPercent(item.margem)}`;
            }
            return [
              `Venda: ${formatMoeda(item.venda)}`,
              `Meta: ${formatMoeda(item.meta)}`,
              `% Atingido: ${formatPercent(item.atingido)}`
            ];
          }
        }
      },
      datalabels: {
        color: (ctx) => (ctx.dataset.type === 'line' ? '#d97706' : '#0f172a'),
        font: { weight: 'bold', size: isMobile ? 7 : 8 },
        anchor: 'end',
        align: 'top',
        offset: 2,
        rotation: (ctx) => (ctx.dataset.type === 'line' ? 0 : -90),
        formatter: (v) => `${(v ?? 0).toFixed(1)}%`
      }
    },
    scales: {
      x: {
        ticks: { font: { size: 8 } },
        grid: { display: false }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        max: 120,
        title: { display: true, text: 'Atingido (%)', font: { weight: '600', size: 10 } },
        ticks: { callback: (val) => val + '%', font: { size: 9 } },
        grid: { color: '#f1f5f9' }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        max: 120,
        title: { display: true, text: 'Margem (%)', font: { weight: '600', size: 10 } },
        ticks: { callback: (val) => val + '%', font: { size: 9 } },
        grid: { drawOnChartArea: false }
      }
    }
  };

  return (
    <div className="flash-vendas-layout-wrapper">
      <Menu modulo="ARBOR" />
      
      <LoadingModal show={loading} message={loadingMessage} />

      <button className="btn-floating-calendar" onClick={() => setShowModal(true)} title="Selecionar Período">
        <FaCalendarAlt />
      </button>

      <div className="container-fluid px-3 px-md-4 max-width-container">
        
        {/* CABEÇALHO COM FILTROS */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pt-2 gap-2">
          <h5 className="fw-bold m-0 text-dark fs-6">
            Vendas por Departamento - Período: <span className="text-primary">{formatarMesExibicao(mes)}</span>
          </h5>

          <div className="d-flex align-items-center gap-3">
            {/* 🔹 CHECKBOX "Somente com meta" */}
            <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
              <input 
                className="form-check-input cursor-pointer" 
                type="checkbox" 
                id="checkSomenteMeta" 
                checked={somenteComMeta} 
                onChange={(e) => setSomenteComMeta(e.target.checked)} 
              />
              <label className="form-check-label small fw-semibold text-secondary cursor-pointer" htmlFor="checkSomenteMeta">
                <FaFilter className="me-1 text-primary" /> Somente com meta
              </label>
            </div>

            <button className="btn btn-outline-secondary btn-sm fw-semibold" onClick={() => carregarDepto()} disabled={loading}>
              <FaUndoAlt className="me-1" /> Reset
            </button>
          </div>
        </div>

        {/* PAINEL DO GRÁFICO */}
        <div className="chart-container position-relative mb-4">
          <div style={{ width: "100%", height: isMobile ? 220 : 260 }}>
            <Bar options={chartOptions} data={chartData} />
          </div>
        </div>

        {/* 🔹 RESUMO GERAL MOBILE PADRONIZADO E SEM QUEBRA DE TEXTO */}
        {isMobile && departamentosFiltrados.length > 0 && (
          <div className="card mb-3 border shadow-sm rounded-3 bg-white p-3">
            <div className="small text-uppercase text-muted text-center fw-bold mb-2 pb-1 border-bottom">
              Resumo Geral
            </div>
            <div className="row text-center g-2 align-items-center">
              <div className="col-5 border-end pe-1">
                <span className="x-small text-muted d-block">Venda</span>
                <strong className="text-dark font-mono-compact d-block text-truncate">
                  {formatMoeda(resumoGeral.venda)}
                </strong>
              </div>
              <div className="col-3 border-end px-1">
                <span className="x-small text-muted d-block">Atingido</span>
                <strong className={`fs-6 ${atingidoGeral >= 100 ? "text-success" : "text-custom-red"}`}>
                  {formatPercent(atingidoGeral)}
                </strong>
              </div>
              <div className="col-4 ps-1">
                <span className="x-small text-muted d-block">Margem</span>
                <strong className="fs-6 text-dark">{formatPercent(margemGeral)}</strong>
              </div>
            </div>
          </div>
        )}

        {/* VISÃO DESKTOP (Tabela) */}
        {!isMobile && (
          <div className="table-frozen-wrapper shadow-sm border rounded-3 mb-4">
            <table className="table table-striped table-hover align-middle m-0 table-compact-fixed">
              <thead className="table-header-frozen">
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Descrição</th>
                  <th className="text-end" style={{ width: '130px' }}>Venda</th>
                  <th className="text-end" style={{ width: '130px' }}>Meta</th>
                  <th className="text-end" style={{ width: '100px' }}>% Ating.</th>
                  <th className="text-end" style={{ width: '100px' }}>Margem</th>
                  <th className="text-center" style={{ width: '80px' }}>Progresso</th>
                </tr>
              </thead>
              <tbody>
                {/* LINHA DE TOTAL NO TOPO */}
                <tr className="fw-bold table-secondary border-bottom">
                  <td></td>
                  <td className="text-uppercase text-primary">TOTAL GERAL</td>
                  <td className="text-end text-primary">{formatMoeda(resumoGeral.venda)}</td>
                  <td className="text-end">{formatMoeda(resumoGeral.meta)}</td>
                  <td className={`text-end ${atingidoGeral >= 100 ? "text-success" : "text-custom-red"}`}>
                    {formatPercent(atingidoGeral)}
                  </td>
                  <td className="text-end text-dark">{formatPercent(margemGeral)}</td>
                  <td className="text-center py-1">
                    <MetaProgressoGarrafa percentual={atingidoGeral} />
                  </td>
                </tr>

                {departamentosFiltrados.map((d) => (
                  <React.Fragment key={d.dep}>
                    <tr onClick={() => buscarSecao(d.dep, d.ddepartamento)} style={{ cursor: "pointer" }} className="fw-semibold">
                      <td className="text-center">
                        {dadosSecao[d.dep] ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                      </td>
                      <td className="text-dark">{d.ddepartamento}</td>
                      <td className="text-end">{formatMoeda(d.venda)}</td>
                      <td className="text-end">{formatMoeda(d.meta)}</td>
                      <td className={`text-end ${d.atingido >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(d.atingido)}
                      </td>
                      <td className="text-end text-dark">{formatPercent(d.margem)}</td>
                      <td className="text-center py-1">
                        <MetaProgressoGarrafa percentual={d.atingido} />
                      </td>
                    </tr>

                    {/* SEÇÕES EXPANSÍVEIS */}
                    {getSecoesFiltradas(d.dep).map((s) => (
                      <React.Fragment key={s.codsecao}>
                        <tr className="bg-light" onClick={() => buscarLoja(d.dep, s.codsecao, s.descsecao)} style={{ cursor: "pointer" }}>
                          <td className="text-center">
                            {dadosLoja[`${d.dep}-${s.codsecao}`] ? <FaChevronUp size={10}/> : <FaChevronDown size={10}/>}
                          </td>
                          <td style={{ paddingLeft: "32px" }} className="text-primary fw-medium">{s.descsecao}</td>
                          <td className="text-end">{formatMoeda(s.venda)}</td>
                          <td className="text-end">{formatMoeda(s.meta)}</td>
                          <td className={`text-end fw-semibold ${s.atingido >= 100 ? "text-success" : "text-custom-red"}`}>{formatPercent(s.atingido)}</td>
                          <td className="text-end text-dark">{formatPercent(s.margem)}</td>
                          <td className="text-center py-1">
                            <MetaProgressoGarrafa percentual={s.atingido} />
                          </td>
                        </tr>

                        {/* LOJAS EXPANSÍVEIS */}
                        {getLojasFiltradas(`${d.dep}-${s.codsecao}`).map((l, idx) => (
                          <tr key={idx} className="small text-muted bg-white">
                            <td></td>
                            <td style={{ paddingLeft: "60px" }}>Loja {formatLoja(l.filial)}</td>
                            <td className="text-end">{formatMoeda(l.venda)}</td>
                            <td className="text-end">{formatMoeda(l.meta)}</td>
                            <td className={`text-end fw-semibold ${l.atingido >= 100 ? "text-success" : "text-custom-red"}`}>
                              {formatPercent(l.atingido)}
                            </td>
                            <td className="text-end text-dark">{formatPercent(l.margem)}</td>
                            <td className="text-center py-1">
                              <MetaProgressoGarrafa percentual={l.atingido} />
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* VISÃO MOBILE MODERNIZADA */}
        {isMobile && (
          <div className="mobile-list pb-5">
            {departamentosFiltrados.map((d) => (
              <div key={d.dep} className="card mb-3 border-0 shadow-sm rounded-3 overflow-hidden">
                
                {/* DEPARTAMENTO - CABEÇALHO */}
                <div 
                  className="card-header bg-white py-3 px-3 border-0 d-flex justify-content-between align-items-center" 
                  onClick={() => buscarSecao(d.dep, d.ddepartamento)} 
                  style={{ cursor: 'pointer' }}
                >
                  <span className="fw-bold text-dark fs-6">{d.ddepartamento}</span>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`fw-bold fs-6 ${d.atingido >= 100 ? "text-success" : "text-custom-red"}`}>
                      {formatPercent(d.atingido)}
                    </span>
                    {dadosSecao[d.dep] ? <FaChevronUp size={14} className="text-muted"/> : <FaChevronDown size={14} className="text-muted"/>}
                  </div>
                </div>

                {/* DEPARTAMENTO - METRICAS UNIFORMES */}
                <div className="px-3 pb-3">
                  <div className="row g-2 text-center bg-light rounded-3 py-2 border m-0">
                    <div className="col-6 border-end border-bottom pb-1">
                      <span className="text-muted d-block x-small">Venda</span>
                      <strong className="text-dark fs-6">{formatMoeda(d.venda)}</strong>
                    </div>
                    <div className="col-6 border-bottom pb-1">
                      <span className="text-muted d-block x-small">Meta</span>
                      <strong className="text-dark fs-6">{formatMoeda(d.meta)}</strong>
                    </div>
                    <div className="col-6 border-end pt-1">
                      <span className="text-muted d-block x-small">Atingido</span>
                      <strong className={`fs-6 ${d.atingido >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(d.atingido)}
                      </strong>
                    </div>
                    <div className="col-6 pt-1">
                      <span className="text-muted d-block x-small">Margem</span>
                      <strong className="text-dark fs-6">{formatPercent(d.margem)}</strong>
                    </div>
                  </div>
                </div>

                {/* SEÇÕES (NÍVEL 2) */}
                {dadosSecao[d.dep] && (
                  <div className="card-body bg-light border-top pt-2 px-2 pb-2">
                    {getSecoesFiltradas(d.dep).map((s) => (
                      <div key={s.codsecao} className="bg-white rounded-3 p-2 mb-2 border shadow-xs">
                        
                        {/* SEÇÃO - CABEÇALHO */}
                        <div 
                          className="d-flex justify-content-between align-items-center py-1 px-1" 
                          onClick={() => buscarLoja(d.dep, s.codsecao, s.descsecao)} 
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="fw-bold text-primary fs-6">{s.descsecao}</span>
                          <div className="d-flex align-items-center gap-2">
                            <span className={`fw-bold ${s.atingido >= 100 ? "text-success" : "text-custom-red"}`}>
                              {formatPercent(s.atingido)}
                            </span>
                            {dadosLoja[`${d.dep}-${s.codsecao}`] ? <FaChevronUp size={12} className="text-muted"/> : <FaChevronDown size={12} className="text-muted"/>}
                          </div>
                        </div>

                        {/* SEÇÃO - METRICAS UNIFORMES */}
                        <div className="row g-2 text-center bg-light rounded-3 py-2 border m-0 mt-1">
                          <div className="col-6 border-end border-bottom pb-1">
                            <span className="text-muted d-block x-small">Venda</span>
                            <strong className="text-dark small">{formatMoeda(s.venda)}</strong>
                          </div>
                          <div className="col-6 border-bottom pb-1">
                            <span className="text-muted d-block x-small">Meta</span>
                            <strong className="text-dark small">{formatMoeda(s.meta)}</strong>
                          </div>
                          <div className="col-6 border-end pt-1">
                            <span className="text-muted d-block x-small">Atingido</span>
                            <strong className={`small ${s.atingido >= 100 ? "text-success" : "text-custom-red"}`}>
                              {formatPercent(s.atingido)}
                            </strong>
                          </div>
                          <div className="col-6 pt-1">
                            <span className="text-muted d-block x-small">Margem</span>
                            <strong className="text-dark small">{formatPercent(s.margem)}</strong>
                          </div>
                        </div>

                        {/* LOJAS (NÍVEL 3) */}
                        {dadosLoja[`${d.dep}-${s.codsecao}`] && (
                          <div className="mt-2 border-top pt-2">
                            {getLojasFiltradas(`${d.dep}-${s.codsecao}`).map((l, i) => (
                              <div key={i} className="mb-2 p-2 border-start border-primary border-3 bg-white shadow-xs rounded-3">
                                <div className="d-flex justify-content-between align-items-center fw-bold mb-1">
                                  <span className="text-dark small">LOJA {formatLoja(l.filial)}</span>
                                  <span className={l.atingido >= 100 ? "text-success" : "text-custom-red"}>
                                    {formatPercent(l.atingido)}
                                  </span>
                                </div>
                                <div className="row g-1 text-center bg-light rounded py-1 border m-0 x-small">
                                  <div className="col-6 border-end">Venda: <b className="text-dark">{formatMoeda(l.venda)}</b></div>
                                  <div className="col-6">Meta: <b className="text-dark">{formatMoeda(l.meta)}</b></div>
                                  <div className="col-12 border-top pt-1 mt-1">Margem: <b className="text-dark">{formatPercent(l.margem)}</b></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL SELEÇÃO DE PERÍODO */}
      <ModalGenerico
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Selecionar Período"
      >
        <div className="mb-3">
          <label className="form-label fw-bold text-muted small">Selecione o Mês / Ano:</label>
          <input 
            type="month" 
            className="form-control rounded-3" 
            value={formatarParaInput(mes)}
            onChange={(e) => {
              if(!e.target.value) return;
              const [a, m] = e.target.value.split("-");
              const novo = `${a.slice(2)}${m}`;
              setMes(novo); 
              carregarDepto(novo); 
              setShowModal(false);
            }} 
          />
        </div>
      </ModalGenerico>

      <Footer className="footer navbar-fixed-bottom" />
    </div>
  );
};

export default VendaDeptoSecao;